import { useQuery, useQueryClient } from '@tanstack/react-query'
import { resolveOrdinalDescriptor, type IOrdinalDescriptor } from 'api/api'
import { useEffect, useRef } from 'react'
import { useEventBus } from './useEventBus'
import { filter, type Subscription } from 'rxjs'
import type { EventBusPayload } from 'services/eventBusService'

export default function useOrdinalDescriptor(
    outpoint: string | null | undefined,
    opts?: { assumeOrigin?: boolean; originOutpointHint?: string; enabled?: boolean },
): {
    data: IOrdinalDescriptor | null | undefined
    isLoading: boolean
    isError: boolean
    refetch: () => void
} {
    const enabled = (opts?.enabled ?? true) && !!outpoint
    const queryClient = useQueryClient()
    const { getTopic$, isConnected } = useEventBus()
    const subRef = useRef<{ sub1: Subscription; sub2: Subscription } | null>(null)

    const queryKey = [
        'ordinalDescriptor',
        outpoint,
        !!opts?.assumeOrigin,
        opts?.originOutpointHint || '',
    ]

    const q = useQuery({
        queryKey,
        queryFn: () =>
            resolveOrdinalDescriptor(outpoint as string, {
                assumeOrigin: !!opts?.assumeOrigin,
                originOutpointHint: opts?.originOutpointHint,
            }),
        enabled,
        // No polling: we expect a websocket/EventBus update when resolution completes.
        staleTime: 30 * 1000,
    })

    useEffect(() => {
        if (!enabled || !outpoint || !isConnected) return

        // Subscribe to updates and refetch to get canonical descriptor from Rails.
        // Payload shape (server): { outpoint, status, origin_outpoint, bsv_ordinal_id, ... }
        const sub1 = getTopic$('ordinals.resolve')
            .pipe(filter((payload: EventBusPayload) => payload?.outpoint === outpoint))
            .subscribe({
                next: () => queryClient.invalidateQueries({ queryKey }),
            })

        const sub2 = getTopic$('ordinals.preview_cached')
            .pipe(filter((payload: EventBusPayload) => payload?.outpoint === outpoint))
            .subscribe({
                next: () => queryClient.invalidateQueries({ queryKey }),
            })

        subRef.current = { sub1, sub2 }

        return () => {
            try {
                subRef.current?.sub1?.unsubscribe?.()
                subRef.current?.sub2?.unsubscribe?.()
            } catch {}
            subRef.current = null
        }
    }, [
        enabled,
        outpoint,
        isConnected,
        getTopic$,
        queryClient,
        opts?.assumeOrigin,
        opts?.originOutpointHint,
    ])

    return {
        data: q.data,
        isLoading: q.isLoading,
        isError: q.isError,
        refetch: () => q.refetch(),
    }
}
